import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Heading, Text, useToast } from "@chakra-ui/react";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import useThemeColors from "../../../../../hooks/useThemeColors";
import SaveButtons from "./SaveButtons";
import { textSecondary } from "../../../../../utils/colors";
import { TENANT_UPDATE_SCHEDULING_SETTINGS_RESET } from "../../../../../constants/tenantConstants";
import { updateTenantSchedulingSettingsAction } from "../../../../../actions/tenantActions";
import { customSelectStyles } from "../../../../../components/input/customStyles";

const getTimezoneOptions = () => {
  if (
    typeof Intl !== "undefined" &&
    typeof Intl.supportedValuesOf === "function"
  ) {
    return Intl.supportedValuesOf("timeZone").map((zone) => ({
      value: zone,
      label: zone.replace(/_/g, " "),
    }));
  }

  const fallback = [
    "UTC",
    "Europe/Vilnius",
    "Europe/London",
    "America/New_York",
    "America/Los_Angeles",
    "Asia/Dubai",
  ];

  return fallback.map((zone) => ({
    value: zone,
    label: zone.replace(/_/g, " "),
  }));
};

const normalize = (value, fallback = "") =>
  value === undefined || value === null ? fallback : String(value).trim();

const ClinicSchedulingSettings = ({ slug, tenant }) => {
  const { backgroundLightMode, newBorderMode } = useThemeColors();
  const toast = useToast();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [timezone, setTimezone] = useState(tenant?.timezone || "UTC");
  const [timeFormat, setTimeFormat] = useState(tenant?.timeFormat || "24h");
  const [weekStart, setWeekStart] = useState(tenant?.weekStart || "monday");
  const [showButtons, setShowButtons] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const timezoneOptions = useMemo(() => getTimezoneOptions(), []);

  const timeFormatOptions = useMemo(
    () => [
      {
        value: "24h",
        label: t("clinicSettings.timeFormat24h", "24-hour clock"),
      },
      {
        value: "12h",
        label: t("clinicSettings.timeFormat12h", "12-hour clock (AM/PM)"),
      },
    ],
    [t]
  );

  const weekStartOptions = useMemo(
    () => [
      { value: "monday", label: t("clinicSettings.weekStartMonday", "Monday") },
      { value: "sunday", label: t("clinicSettings.weekStartSunday", "Sunday") },
    ],
    [t]
  );

  const tenantUpdateSchedulingSettings = useSelector(
    (state) => state.tenantUpdateSchedulingSettings
  );
  const { success, error } = tenantUpdateSchedulingSettings;

  const timezoneValue = useMemo(() => {
    const option = timezoneOptions.find((item) => item.value === timezone);
    return option || (timezone ? { value: timezone, label: timezone } : null);
  }, [timezone, timezoneOptions]);

  const timeFormatValue = useMemo(
    () => timeFormatOptions.find((item) => item.value === timeFormat) || null,
    [timeFormat, timeFormatOptions]
  );

  const weekStartValue = useMemo(
    () => weekStartOptions.find((item) => item.value === weekStart) || null,
    [weekStart, weekStartOptions]
  );

  useEffect(() => {
    const baseTimezone = normalize(tenant?.timezone, "UTC");
    const baseFormat = normalize(tenant?.timeFormat, "24h");
    const baseWeekStart = normalize(tenant?.weekStart, "monday");

    const changed =
      normalize(timezone) !== baseTimezone ||
      normalize(timeFormat) !== baseFormat ||
      normalize(weekStart) !== baseWeekStart;

    setShowButtons(changed);
  }, [timezone, timeFormat, weekStart, tenant]);

  useEffect(() => {
    if (success && isSaving) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsUpdate",
          "Scheduling settings updated"
        ),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setShowButtons(false);
    } else if (error && isSaving) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsUpdateError",
          "Unable to update scheduling settings"
        ),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }

    if (success || error) {
      setIsSaving(false);
      dispatch({ type: TENANT_UPDATE_SCHEDULING_SETTINGS_RESET });
    }
  }, [success, error, dispatch, toast, t, isSaving]);

  useEffect(() => {
    setTimezone(tenant?.timezone || "UTC");
    setTimeFormat(tenant?.timeFormat || "24h");
    setWeekStart(tenant?.weekStart || "monday");
  }, [tenant?.timezone, tenant?.timeFormat, tenant?.weekStart]);

  const handleSave = () => {
    if (!timezone) {
      toast({
        title: t(
          "toasts.clinicSchedulingSettingsValidation",
          "Please complete all required fields"
        ),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    dispatch(
      updateTenantSchedulingSettingsAction(slug, tenant.tenantID, {
        timezone,
        timeFormat,
        weekStart,
      })
    );
  };

  const handleCancel = () => {
    setTimezone(tenant?.timezone || "UTC");
    setTimeFormat(tenant?.timeFormat || "24h");
    setWeekStart(tenant?.weekStart || "monday");
    setShowButtons(false);
  };

  return (
    <Box
      w="100%"
      p={["1.5rem 0", "3rem 0rem 3rem"]}
      borderBottom={`1px solid ${newBorderMode}`}
    >
      <Flex
        w="100%"
        justify="space-between"
        gap={["1.5rem", "2rem"]}
        direction={["column", "row"]}
      >
        <Flex direction="column" gap={2} w={["100%", "40%"]}>
          <Heading fontSize="1rem" fontWeight="600" p={["0 1.5rem", "initial"]}>
            {t(
              "clinicSettings.clinicSchedulingSettings",
              "Clinic scheduling settings"
            )}
          </Heading>
          <Text
            color={textSecondary}
            fontSize="sm"
            fontWeight="500"
            p={["0 1.5rem", "initial"]}
          >
            {t(
              "clinicSettings.clinicSchedulingSettingsText",
              "Configure the default calendar settings for your clinic"
            )}
          </Text>
        </Flex>

        <Flex
          w={["100%", "800px"]}
          direction="column"
          justify="flex-start"
          align="flex-start"
        >
          <Flex
            w="100%"
            bg={backgroundLightMode}
            borderRadius="1rem"
            p="1.25rem 1.5rem 1.25rem"
            direction="column"
            gap="1.25rem"
          >
            <Flex w="100%" gap={["1rem", "2rem"]} direction={["column", "row"]}>
              <Box w={["100%", "50%", "50%"]}>
                <Text fontSize="0.8rem" fontWeight="500" mb={2}>
                  {t("form.timezone", "Default timezone")}
                </Text>
                <Select
                  isSearchable={false}
                  options={timezoneOptions}
                  value={timezoneValue}
                  onChange={(option) => setTimezone(option?.value || "")}
                  styles={customSelectStyles}
                  placeholder={t("form.selectTimezone", "Select timezone")}
                />
              </Box>

              <Box w={["100%", "50%", "50%"]}>
                <Text fontSize="0.8rem" fontWeight="500" mb={2}>
                  {t("form.timeFormat", "Time format")}
                </Text>
                <Select
                  isSearchable={false}
                  options={timeFormatOptions}
                  value={timeFormatValue}
                  onChange={(option) => setTimeFormat(option?.value || "24h")}
                  styles={customSelectStyles}
                  placeholder={t("form.selectTimeFormat", "Select format")}
                />
              </Box>
            </Flex>

            <Box w={["100%", "50%", "40%"]}>
              <Text fontSize="0.8rem" fontWeight="500" mb={2}>
                {t("form.weekStart", "Week starts on")}
              </Text>
              <Select
                isSearchable={false}
                options={weekStartOptions}
                value={weekStartValue}
                onChange={(option) => setWeekStart(option?.value || "monday")}
                styles={customSelectStyles}
                placeholder={t(
                  "form.selectWeekStart",
                  "Select start of week"
                )}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>

      {showButtons && (
        <SaveButtons onClose={handleCancel} onClick={handleSave} />
      )}
    </Box>
  );
};

export default ClinicSchedulingSettings;
